'use server'

import { apiRequest, ApiResponse } from '../request'

export type MarketingEventTrigger =
    | 'STAGE_ENTERED'
    | 'ORDER_FIRST_PAYMENT'
    | 'ORDER_SETTLED'
    | 'APPOINTMENT_SCHEDULED'
    | 'LEAD_CREATED'
    | 'APPOINTMENT_CANCELLED'

export type MarketingEventDestination = 'META' | 'GOOGLE_ADS' | 'TIKTOK'

export type MarketingEventValueSource =
    | 'NONE'
    | 'ORDER_TOTAL'
    | 'BILLING_PAID_TOTAL'
    | 'DEAL_VALUE'

export type MarketingEventDispatchStatus = 'SENT' | 'FAILED' | 'SKIPPED'

export interface MarketingEventMapping {
    id: number
    organizationId: number
    trigger: MarketingEventTrigger
    pipelineId: number | null
    stageId: number | null
    destination: MarketingEventDestination
    eventName: string
    valueSource: MarketingEventValueSource
    active: boolean
    createdAt: string
    updatedAt: string
}

export interface MarketingEventDispatch {
    id: number
    destination: MarketingEventDestination
    eventName: string
    eventKey: string
    dealId: number | null
    orderId: number | null
    billingId: number | null
    leadId: number | null
    occurredAt: string
    status: MarketingEventDispatchStatus
    reason: string | null
    createdAt: string
}

export interface MarketingEventMappingData {
    trigger: MarketingEventTrigger
    pipelineId?: number | null
    stageId?: number | null
    destination: MarketingEventDestination
    eventName: string
    valueSource?: MarketingEventValueSource
    active?: boolean
}

export const MARKETING_EVENT_TRIGGER_LABELS: Record<MarketingEventTrigger, string> = {
    STAGE_ENTERED: 'Entrou na etapa do funil',
    ORDER_FIRST_PAYMENT: 'Entrada paga (primeiro pagamento)',
    ORDER_SETTLED: 'Venda fechada (pagamento total)',
    LEAD_CREATED: 'Lead criado',
    APPOINTMENT_SCHEDULED: 'Agendamento marcado',
    APPOINTMENT_CANCELLED: 'Atendimento cancelado',
}

export const MARKETING_EVENT_TRIGGER_HINTS: Record<MarketingEventTrigger, string> = {
    STAGE_ENTERED:
        'Dispara quando a oportunidade entra na etapa escolhida. Sem funil e etapa, vale para qualquer uma. O orçamento aprovado chega por aqui: o funil move o card sozinho.',
    ORDER_FIRST_PAYMENT:
        'Dispara no primeiro pagamento de uma venda paga em partes, com o valor TOTAL da venda. Mapeado junto com "venda fechada" e com o mesmo nome de evento, a plataforma recebe uma conversão só: a primeira que acontecer.',
    ORDER_SETTLED:
        'Só quando o procedimento é pago por inteiro. No flex e no parcelado cada pagamento é uma cobrança, e uma compra por cobrança inflaria a contagem.',
    LEAD_CREATED: 'Dispara quando o lead entra, inclusive o que vem de anúncio sem filial.',
    APPOINTMENT_SCHEDULED:
        'Dispara quando a data do atendimento é definida (consulta, exame ou procedimento), no momento em que a data entra na agenda — não na data marcada.',
    APPOINTMENT_CANCELLED: 'Dispara quando o procedimento é cancelado.',
}

export const MARKETING_EVENT_DESTINATION_LABELS: Record<MarketingEventDestination, string> = {
    META: 'Meta (Facebook e Instagram)',
    GOOGLE_ADS: 'Google Ads',
    TIKTOK: 'TikTok',
}

export const MARKETING_EVENT_VALUE_SOURCE_LABELS: Record<MarketingEventValueSource, string> = {
    NONE: 'Sem valor',
    ORDER_TOTAL: 'Valor total do procedimento',
    BILLING_PAID_TOTAL: 'Valor pago na cobrança',
    DEAL_VALUE: 'Valor da oportunidade',
}

export const MARKETING_EVENT_STATUS_LABELS: Record<MarketingEventDispatchStatus, string> = {
    SENT: 'Enviado',
    FAILED: 'Falhou',
    SKIPPED: 'Não enviado',
}

export const MARKETING_EVENT_STATUS_COLORS: Record<MarketingEventDispatchStatus, string> = {
    SENT: 'badge-success',
    FAILED: 'badge-error',
    SKIPPED: 'badge-ghost',
}

/** Nomes que a Meta reconhece como padrão; o operador pode usar outro. */
export const META_STANDARD_EVENT_NAMES = [
    'Purchase',
    'Lead',
    'Schedule',
    'CompleteRegistration',
    'InitiateCheckout',
    'Contact',
]

const BASE = 'marketing/events'

export async function getMarketingEventMappings(): Promise<ApiResponse<MarketingEventMapping[]>> {
    return apiRequest<MarketingEventMapping[]>(`${BASE}/mappings`, {})
}

export async function createMarketingEventMapping(
    data: MarketingEventMappingData,
): Promise<ApiResponse<MarketingEventMapping>> {
    return apiRequest<MarketingEventMapping>(`${BASE}/mappings`, { data }, 'post')
}

export async function updateMarketingEventMapping(
    id: number,
    data: Partial<MarketingEventMappingData>,
): Promise<ApiResponse<MarketingEventMapping>> {
    return apiRequest<MarketingEventMapping>(`${BASE}/mappings/${id}`, { data }, 'patch')
}

export async function deleteMarketingEventMapping(
    id: number,
): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(`${BASE}/mappings/${id}`, {}, 'delete')
}

export async function createDefaultMarketingEventMappings(): Promise<
    ApiResponse<MarketingEventMapping[]>
> {
    return apiRequest<MarketingEventMapping[]>(`${BASE}/mappings/defaults`, {}, 'post')
}

export async function getMarketingEventDispatches(filters?: {
    status?: MarketingEventDispatchStatus
    limit?: number
}): Promise<ApiResponse<MarketingEventDispatch[]>> {
    return apiRequest<MarketingEventDispatch[]>(`${BASE}/dispatches`, {
        params: filters as any,
    })
}
